import {
  Conversation,
  ConversationType,
  getInitials,
  User,
} from '@nextforce/api'
import { useRoute } from '@react-navigation/native'
import { Avatar, ListItem, makeStyles, Text } from '@rneui/themed'
import { font } from 'app/theme'
import parse from 'autosuggest-highlight/parse'
import { Image } from 'expo-image'
import { isImageType, isVideoType } from 'file_folder_repository/util'
import { compact } from 'lodash'
import React, { useCallback } from 'react'
import {
  ImageSourcePropType,
  Platform,
  StyleProp,
  View,
  ViewStyle,
} from 'react-native'
import { getBackgroundColorById } from 'shared/components/user/user_avatar'
import { stripInvalidCodepoints } from 'shared/utils'

import { CheckBox } from 'shared/components/check_box'
import Highlighter from 'shared/wrappers/react-native-highlight-words'
import { getConversationUnread, isRepository } from '../utils'
interface Props {
  conversation: Conversation
  user: User | undefined
  onPressConversation: (conversation: Conversation, messageId?: string) => void
  onLongPress: (conversation: Conversation) => void
  highlightSenderName?: boolean
  selectable?: boolean
  checked?: boolean
  urgent?: boolean
}

const CUSTOM_ICON_GROUP_TYPES: ConversationType[] = [
  'companyManagers',
  'coverRequests',
  'companyAnnouncements',
  'companyLeaderboard',
  'repository',
]

function ConversationCard({
  conversation,
  user,
  onPressConversation,
  onLongPress,
  highlightSenderName,
  selectable,
  checked = false,
  urgent = false,
}: Props) {
  const tab = useRoute().name
  const hideSender = tab === 'marked' || tab === 'sent'

  const styles = useStyles()
  const { name } = useRoute()
  const isMention = !!conversation.status?.recentMessage?.mentions?.find(
    mention => mention.user.id === user?.id
  )

  const onPress = useCallback(
    () =>
      onPressConversation(conversation, conversation.status?.recentMessage?.id),
    [conversation, onPressConversation]
  )

  const recentMessage = getRecentMessage(conversation, hideSender)
  const senderName = getSenderName(conversation, hideSender)

  const parses = parse(
    recentMessage ? recentMessage : '',
    conversation.status?.recentMessage?.matches
      ? conversation.status?.recentMessage?.matches
      : []
  )
  const highlight = compact(
    parses.map(p => {
      return p.highlight ? p.text : null
    })
  )

  return (
    <ListItem onPress={onPress} onLongPress={() => onLongPress(conversation)}>
      <ConversationIcon conversation={conversation} />
      <ListItem.Content>
        <ListItem.Title
          style={styles.name}
          numberOfLines={2}
          textBreakStrategy="simple"
        >
          {stripInvalidCodepoints(
            conversation.name +
              ((tab === 'marked' && conversation.type !== 'dm') ||
              (conversation.status?.recentMessage?.isMarked && highlight.length)
                ? ', ' +
                  conversation.status?.recentMessage?.user.firstName +
                  ' ' +
                  conversation.status?.recentMessage?.user.lastName
                : '')
          )}
        </ListItem.Title>
        {recentMessage ? (
          <ListItem.Subtitle
            style={styles.recentMessage}
            numberOfLines={2}
            textBreakStrategy="simple"
          >
            {highlight.length && Platform.OS !== 'web' ? (
              <Highlighter
                autoEscape
                highlightStyle={{ backgroundColor: 'yellow' }}
                searchWords={highlight}
                textToHighlight={recentMessage}
              />
            ) : (
              recentMessage
            )}
          </ListItem.Subtitle>
        ) : null}
        {senderName ? (
          <ListItem.Subtitle
            style={
              highlightSenderName ? styles.highlight : styles.recentMessage
            }
          >
            {senderName}
          </ListItem.Subtitle>
        ) : null}
      </ListItem.Content>
      {selectable ? (
        <CheckBox checked={checked} onPress={onPress} />
      ) : (
        <>
          {name !== 'marked' && getConversationUnread(conversation, user) ? (
            <View style={styles.badge} />
          ) : null}
          {isMention ? (
            <View style={styles.statusIconContainer}>
              <Text style={styles.statusIcon}>@</Text>
            </View>
          ) : null}
          {urgent ? (
            <View style={styles.statusIconContainer}>
              <Text style={styles.statusIcon}>!</Text>
            </View>
          ) : null}
          {name !== 'marked' && conversation.muted ? (
            <Image
              style={styles.mutedIcon}
              source={require('assets/chat/muted.png')}
            />
          ) : null}
          {conversation.status?.recentMessage?.isMarked &&
          (hideSender || highlight.length) ? (
            <Image
              contentFit="contain"
              style={styles.bookmarkIcon}
              source={require('assets/chat/bookmark.png')}
            />
          ) : null}
        </>
      )}
    </ListItem>
  )
}

function getConversationInitials(conversation: Conversation) {
  if (conversation.type === 'dm') {
    if (!conversation.name) {
      return 'DM'
    }
    if (conversation.initials) {
      return stripInvalidCodepoints(conversation.initials)
    }
    const tokens = conversation.name.split(' ')
    const firstName = tokens[0]
    const lastName = tokens[tokens.length - 1]
    return stripInvalidCodepoints(getInitials(firstName, lastName))
  } else if (conversation.type === 'companyManagers') {
    return 'M'
  } else if (conversation.type === 'coverRequests') {
    return 'C'
  } else if (conversation.type === 'companyAnnouncements') {
    return 'N'
  } else if (conversation.type === 'companyLeaderboard') {
    return 'L'
  }
}

export function ConversationIcon({
  conversation,
  containerStyle,
  children,
  size = 'medium',
}: {
  conversation: Conversation
  containerStyle?: StyleProp<ViewStyle>
  children?: React.ReactNode
  size?: number | 'small' | 'medium' | 'large' | 'xlarge' | undefined
}) {
  const styles = useStyles()
  const iconSource = getIconSource(conversation)
  const isCustomIcon = isCustomIconGroupType(conversation)
  const isRepositoryConversation = isRepository(conversation)
  const backgroundColor = getBackgroundColorById(conversation.id)

  return isRepositoryConversation ? (
    <Avatar
      rounded
      containerStyle={{ backgroundColor }}
      size={size}
      icon={{ name: 'folder', type: 'material-design' }}
    />
  ) : (
    <Avatar
      rounded
      size={size}
      source={iconSource}
      title={iconSource ? undefined : getConversationInitials(conversation)}
      titleStyle={isCustomIcon ? styles.customInitials : styles.initials}
      containerStyle={[
        containerStyle,
        isCustomIcon ? styles.customIconContainer : styles.iconContainer,
      ]}
    >
      {children}
    </Avatar>
  )
}

function getIconSource(
  conversation: Conversation
): ImageSourcePropType | undefined {
  if (isCustomIconGroupType(conversation)) {
    return
  }
  if (conversation.iconUrl) {
    return { uri: conversation.iconUrl, cache: 'force-cache' }
  }

  if (conversation.type === 'dm') {
    return
  }

  return require('assets/chat/default-group-icon.png')
}

function getRecentMessage(conversation: Conversation, hideSender?: boolean) {
  const recentMessage = conversation.status?.recentMessage

  if (!recentMessage) {
    return null
  }

  let text: string

  if (recentMessage.body) {
    text = recentMessage.body
  } else {
    const mimeType = recentMessage.attachment?.mimeType
    let fileType = 'File'

    if (mimeType) {
      if (isImageType(mimeType)) {
        fileType = 'Photo'
      } else if (isVideoType(mimeType)) {
        fileType = 'Video'
      }
    }

    text = `${fileType} posted`
  }

  return stripInvalidCodepoints(text)
}

function getSenderName(conversation: Conversation, hideSender?: boolean) {
  const recentMessage = conversation.status?.recentMessage

  if (!recentMessage) {
    return null
  }
  let name: string = ''

  if (conversation.type !== 'dm' && !hideSender) {
    name = `${recentMessage.user.firstName} ${recentMessage.user.lastName}`
  }

  return stripInvalidCodepoints(name)
}

function isCustomIconGroupType(conversation: Conversation) {
  return CUSTOM_ICON_GROUP_TYPES.includes(conversation.type)
}

const useStyles = makeStyles(theme => ({
  name: {
    fontFamily: font.bold,
    lineHeight: 19,
  },
  recentMessage: {
    color: theme.colors?.grey3,
  },
  icon: {
    width: 48,
    height: 48,
    borderRadius: 48 / 2,
  },
  badge: {
    padding: 3,
    borderRadius: 12,
    height: 24,
    width: 24,
    backgroundColor: theme.colors?.primary,
  },
  badgeText: {
    color: theme.colors?.white,
    textAlign: 'center',
  },
  mutedIcon: {
    width: 23,
    height: 25,
    marginHorizontal: 10,
  },
  bookmarkIcon: {
    width: 18,
    height: 22,
  },
  statusIcon: {
    textAlignVertical: 'center',
    fontSize: 24,
    color: theme.colors.primary,
  },
  highlight: {
    fontFamily: font.bold,
    color: theme.colors?.grey3,
  },
  initials: { color: theme.colors.grey1 },
  iconContainer: { backgroundColor: theme.colors?.grey5 },
  customInitials: { color: 'white' },
  customIconContainer: { backgroundColor: theme.colors?.grey8 },
  checkIcon: {
    color: theme.colors.primary,
    backgroundColor: theme.colors.white,
    zIndex: 1,
    right: -10,
    bottom: -5,
    elevation: 0,
  },
  statusIconContainer: { marginLeft: 5 },
}))

export default ConversationCard
